import * as React from 'react';
import { useSelector } from 'react-redux';
import { Button, Theme } from '@cian/ui-kit';
import { Microfrontend, Provider as MicrofrontendProvider } from '@cian/mf-react';
import type { IRegistry } from '@cian/mf-registry/shared';

import { NewbuildingBrokerFallbackModal } from '../../../components/NewbuildingBanners/NewbuildingBrokerBanner/NewbuildingBrokerFallbackModal';
import { selectIsAuthenticated } from '../../../selectors/isAuthenticated';
import { fetchUser, requestAuthentication } from '../../../thunks';
import { useAppDispatch } from '../../../types';
import {
  useFallbackModalSettings,
  useMicrofrontAPI,
  useMicrofrontendsRegistryRef,
  useMicrofrontEvents,
  useRegion,
} from './hooks';
import { trackNewbuildingBrokerBannerButtonClick } from './tracking';
import { TOpenMicrofrontResultHandler } from './types';

interface INewbuildingBrokerBannerButtonContainerProps {
  text?: string | null;
}

const DEFAULT_BUTTON_TEXT = 'К заявке';

export const NewbuildingBrokerBannerButtonContainer: React.FC<INewbuildingBrokerBannerButtonContainerProps> = ({
  text,
}) => {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const region = useRegion();

  const [isLoading, setIsLoading] = React.useState(false);
  const fallbackModalSettings = useFallbackModalSettings();

  const [isFallbackModalVisible, setFallbackModalVisibility] = React.useState(false);
  const closeFallbackModal = React.useCallback(() => setFallbackModalVisibility(false), []);
  const showFallbackModal = React.useCallback(() => setFallbackModalVisibility(true), []);

  const dispatch = useAppDispatch();

  const finishMicrofrontOpening = React.useCallback<TOpenMicrofrontResultHandler>(
    ({ isError }) => {
      if (isError) {
        showFallbackModal();
        setIsLoading(false);
      } else {
        if (isFallbackModalVisible) {
          closeFallbackModal();
        }

        // снятие loading произойдет по событию из микрофронта.
      }
    },
    [isFallbackModalVisible, showFallbackModal, closeFallbackModal],
  );

  const microfrontendsRegistryRef = useMicrofrontendsRegistryRef();
  const { openMicrofront, microfrontManifest, microfrontRef } = useMicrofrontAPI(
    finishMicrofrontOpening,
    microfrontendsRegistryRef,
  );

  useMicrofrontEvents(microfrontManifest, setIsLoading);

  const startMicrofrontOpening = React.useCallback(async () => {
    setIsLoading(true);
    const user = await dispatch(fetchUser()).unwrap();
    openMicrofront({ user: user ?? {}, region, gaLabel: '' });
  }, [dispatch, region, openMicrofront]);

  const handleButtonClick = React.useCallback(() => {
    trackNewbuildingBrokerBannerButtonClick();

    if (!isAuthenticated) {
      dispatch(
        requestAuthentication({
          onSuccess: startMicrofrontOpening,
        }),
      );

      return;
    }

    startMicrofrontOpening();
  }, [dispatch, isAuthenticated, startMicrofrontOpening]);

  return (
    <>
      <Theme theme="colored">
        <Button fullWidth size="L" loading={isLoading} onClick={handleButtonClick}>
          {text ?? DEFAULT_BUTTON_TEXT}
        </Button>
      </Theme>
      {isFallbackModalVisible && (
        <NewbuildingBrokerFallbackModal
          {...fallbackModalSettings}
          loading={isLoading}
          onClose={closeFallbackModal}
          onRetry={startMicrofrontOpening}
        />
      )}
      {microfrontManifest && (
        <MicrofrontendProvider registry={microfrontendsRegistryRef.current as IRegistry}>
          <Microfrontend
            ref={microfrontRef}
            name={microfrontManifest.microfrontendName}
            image={microfrontManifest.imageVersion}
            runtime={microfrontManifest.runtimeName}
            instance={microfrontManifest.instanceId}
          />
        </MicrofrontendProvider>
      )}
    </>
  );
};
